import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import {
  Col,
  DatePicker,
  Dropdown,
  Menu,
  Row,
  Table,
  Tooltip,
  notification,
} from "antd";

import MenuIcon from "../../../components/utils/table/icons/menu";
import { Container as MenuContainer } from "../../../components/utils/table/menu/styles";
import TagStatus from "../../../components/utils/tagStatus";
import DrawerFeedingAutomationDetails from "../../../components/drawers/feedingAutomationDetails";
import { numberMask } from "../../../services/helpersMethodsService";
import { indexFeedingAutomation } from "../../../services/feedingAutomationService";

const Column = Table.Column;
const { RangePicker } = DatePicker;

const DEFAULT_PERIOD_DAYS = 7;

const SITUATION_COLORS = {
  finished: {
    background: "#C5F1CA",
    borderColor: "#106518",
    color: "#106518",
  },
  open: {
    background: "#F6FFC8",
    borderColor: "#A9C133",
    color: "#A9C133",
  },
  cancelled: {
    background: "#FBC7C7",
    borderColor: "#D44C4C",
    color: "#D44C4C",
  },
};

const STOCK_STATUS_COLORS = {
  Applied: {
    background: "#C5F1CA",
    borderColor: "#106518",
    color: "#106518",
  },
  Pending: {
    background: "#F6FFC8",
    borderColor: "#A9C133",
    color: "#A9C133",
  },
  Processing: {
    background: "#F6FFC8",
    borderColor: "#A9C133",
    color: "#A9C133",
  },
  AppliedWithoutBalance: {
    background: "#FFF7E6",
    borderColor: "#FAAD14",
    color: "#D48806",
  },
  Blocked: {
    background: "#FBC7C7",
    borderColor: "#D44C4C",
    color: "#D44C4C",
  },
  Reversed: {
    background: "#EBEBEB",
    borderColor: "#A5A5A5",
    color: "#6B6B6B",
  },
};

const FeedingAutomationTable = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [detailsId, setDetailsId] = useState(null);
  const [period, setPeriod] = useState([
    moment().subtract(DEFAULT_PERIOD_DAYS - 1, "days"),
    moment(),
  ]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const t = translation.feedingAutomation;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data: response } = await indexFeedingAutomation({
        groupId,
        farmId,
        startDate: period?.[0]?.format("YYYY-MM-DD"),
        endDate: period?.[1]?.format("YYYY-MM-DD"),
        page,
        size,
      });
      setData(response?.results ?? null);
    } catch (error) {
      console.error(error);
      notification.error({ message: t.messages.errorLoad });
    } finally {
      setIsLoading(false);
    }
  }, [groupId, farmId, period, page, size, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTableChange = (pagination) => {
    setPage(pagination.current - 1);
    setSize(pagination.pageSize);
  };

  const handlePeriodChange = (dates) => {
    setPeriod(dates);
    setPage(0);
  };

  const stockStatusTitle = (record) => {
    if (record.stockStatusReason == null) {
      return t.stockStatusHelp?.[record.stockStatus] ?? "";
    }

    const reason = (
      t.stockReason?.[record.stockStatusReason] ??
      t.stockReason?.UNEXPECTED_ERROR ??
      ""
    ).replace("{{name}}", record.stockStatusDetail ?? "");

    if (record.stockStatus === "Blocked") {
      return `${t.stockStatusHelp?.Blocked ?? ""} ${reason}`.trim();
    }

    return reason;
  };

  const menu = (record) => (
    <Menu>
      <Menu.Item key="details" onClick={() => setDetailsId(record.id)}>
        {translation.table.menu.details}
      </Menu.Item>
    </Menu>
  );

  const expandedRow = (day) => (
    <Table
      rowKey="id"
      size="small"
      pagination={false}
      dataSource={day.items ?? []}
      style={{ width: "100%" }}
    >
      <Column
        title={t.columns.time}
        dataIndex="manufactureDatetime"
        key="manufactureDatetime"
        render={(value) => (value ? moment(value).format("HH:mm") : "-")}
      />
      <Column
        title={t.columns.treatNumber}
        dataIndex="treatNumber"
        key="treatNumber"
        align="center"
        render={(value) => value ?? "-"}
      />
      <Column
        title={t.columns.supplementName}
        dataIndex="supplementName"
        key="supplementName"
        render={(value) => value ?? "-"}
      />
      <Column
        title={t.columns.wagonName}
        dataIndex="wagonName"
        key="wagonName"
        render={(value) => value ?? "-"}
      />
      <Column
        title={t.columns.situation}
        dataIndex="situation"
        key="situation"
        render={(value) =>
          value == null ? (
            "-"
          ) : (
            <TagStatus {...(SITUATION_COLORS[value] ?? SITUATION_COLORS.open)}>
              {t.situation[value] ?? value}
            </TagStatus>
          )
        }
      />
      <Column
        title={t.columns.stockStatus}
        dataIndex="stockStatus"
        key="stockStatus"
        render={(value, record) =>
          value == null ? (
            "-"
          ) : (
            <Tooltip title={stockStatusTitle(record)}>
              <TagStatus
                {...(STOCK_STATUS_COLORS[value] ?? STOCK_STATUS_COLORS.Pending)}
              >
                {t.stockStatus?.[value] ?? value}
              </TagStatus>
            </Tooltip>
          )
        }
      />
      <Column
        title={t.columns.quantityPlanned}
        dataIndex="quantityPlanned"
        key="quantityPlanned"
        align="right"
        render={(value) => numberMask(value)}
      />
      <Column
        title={t.columns.quantityManufactured}
        dataIndex="quantityManufactured"
        key="quantityManufactured"
        align="right"
        render={(value) => numberMask(value)}
      />
      <Column
        title={t.columns.deviationPercent}
        dataIndex="deviationPercent"
        key="deviationPercent"
        align="right"
        render={(value) => (value == null ? "-" : `${numberMask(value)}%`)}
      />
      <Column
        align="left"
        render={(text, record) => (
          <Dropdown overlay={menu(record)} trigger={["click"]} key={record.id}>
            <MenuContainer>
              <MenuIcon />
            </MenuContainer>
          </Dropdown>
        )}
      />
    </Table>
  );

  return (
    <>
      <Row
        type="flex"
        justify="start"
        align="middle"
        style={{ marginBottom: 16 }}
      >
        <Col>
          <span
            style={{ marginRight: 8, fontWeight: "bold", color: "#8a50a5" }}
          >
            {t.filters.period}
          </span>
          <RangePicker
            allowClear={false}
            format="DD/MM/YYYY"
            value={period}
            onChange={handlePeriodChange}
          />
        </Col>
      </Row>

      <Table
        loading={isLoading}
        rowKey="day"
        size="small"
        expandedRowRender={expandedRow}
        dataSource={data?.content ?? []}
        locale={{ emptyText: t.emptyText }}
        scroll={{ x: true }}
        onChange={handleTableChange}
        pagination={{
          total: data?.totalElements ?? 0,
          pageSize: data?.size ?? size,
          current: (data?.number ?? 0) + 1,
          showSizeChanger: true,
          pageSizeOptions: ["20", "50", "100"],
        }}
      >
        <Column
          title={t.columns.day}
          dataIndex="day"
          key="day"
          render={(value) => (value ? moment(value).format("DD/MM/YYYY") : "-")}
        />
        <Column
          title={t.columns.manufactures}
          dataIndex="manufactures"
          key="manufactures"
          align="center"
          render={(value) => value ?? 0}
        />
        <Column
          title={t.columns.quantityPlanned}
          dataIndex="quantityPlanned"
          key="quantityPlanned"
          align="right"
          render={(value) => numberMask(value)}
        />
        <Column
          title={t.columns.quantityManufactured}
          dataIndex="quantityManufactured"
          key="quantityManufactured"
          align="right"
          render={(value) => numberMask(value)}
        />
        <Column
          title={t.columns.quantityManufacturedDryMatter}
          dataIndex="quantityManufacturedDryMatter"
          key="quantityManufacturedDryMatter"
          align="right"
          render={(value) => numberMask(value)}
        />
        <Column
          title={t.columns.deviationPercent}
          dataIndex="deviationPercent"
          key="deviationPercent"
          align="right"
          render={(value) => (value == null ? "-" : `${numberMask(value)}%`)}
        />
      </Table>

      <DrawerFeedingAutomationDetails
        visible={detailsId != null}
        manufactureId={detailsId}
        onClose={() => setDetailsId(null)}
      />
    </>
  );
};

export default FeedingAutomationTable;
